import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDashboard } from '../store/useDashboard';
import api from '../services/api';
import {
  IconeListe, IconeNote, IconeAvion, IconeRecu, IconeRafraichir, IconeDossier,
  IconeHorloge, IconeDocument, IconeValide, IconeFermer,
} from '../components/IconesSVG';

const TYPES = {
  expression_besoin: { label: 'Fiche EB',       icon: IconeListe, color: '#2980B9' },
  bon_commande:      { label: 'Bon commande',   icon: IconeNote, color: '#8E44AD' },
  frais_mission:     { label: 'Frais mission',  icon: IconeAvion, color: '#E67E22' },
  facture:           { label: 'Facture',        icon: IconeRecu, color: '#16A085' },
  regularisation:    { label: 'Regularisation', icon: IconeRafraichir, color: '#935116' },
};

export default function ArchivesPage() {
  const navigate = useNavigate();
  const { allerDashboard } = useDashboard();
  const [archives, setArchives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [recherche, setRecherche] = useState('');
  const [filtreFiliale, setFiltreFiliale] = useState('');
  const [filtreType, setFiltreType] = useState('');

  useEffect(() => { charger(); }, []);

  const charger = async () => {
    try {
      setLoading(true);
      const res = await api.get('/demandes?statut=solde');
      setArchives(res.data?.data || res.data || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const formatMontant = (m) => m ? Number(m).toLocaleString('fr-FR') + ' FCFA' : '—';
  const formatDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }) : '—';
  const formatDateHeure = (d) => d ? new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

  const montantTotal = archives.reduce((s, a) => s + (Number(a.montant) || 0), 0);

  const archivesFiltrees = archives.filter(a => {
    const matchRecherche = !recherche || 
      a.reference?.toLowerCase().includes(recherche.toLowerCase()) ||
      (a.titre || a.motif || '').toLowerCase().includes(recherche.toLowerCase()) ||
      (a.demandeur?.prenom + ' ' + a.demandeur?.nom).toLowerCase().includes(recherche.toLowerCase());
    const matchFiliale = !filtreFiliale || a.filiale?.code === filtreFiliale;
    const matchType = !filtreType || a.type_document === filtreType;
    return matchRecherche && matchFiliale && matchType;
  });

  const filiales = [...new Set(archives.map(a => a.filiale?.code).filter(Boolean))];

  return (
    <div style={s.page}>
      {/* HEADER */}
      <div style={s.header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <button onClick={allerDashboard} style={s.backBtn}>← Retour</button>
          <div>
            <div style={{ fontSize: 18, fontWeight: 800, color: '#0D1B2A' }}><IconeDossier taille={17} /> Archives</div>
            <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>
              Dossiers approuvés et scellés par le PDG
            </div>
          </div>
        </div>
        {/* Stats rapides */}
        <div style={{ display: 'flex', gap: 20 }}>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 11, color: '#888' }}>Total dossiers</div>
            <div style={{ fontSize: 20, fontWeight: 800, color: '#27AE60' }}>{archives.length}</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 11, color: '#888' }}>Montant total approuvé</div>
            <div style={{ fontSize: 20, fontWeight: 800, color: '#C0392B' }}>
              {montantTotal.toLocaleString('fr-FR')} FCFA
            </div>
          </div>
        </div>
      </div>

      {/* FILTRES */}
      <div style={s.filtres}>
        <input style={s.search}
          placeholder="Rechercher par référence, titre, demandeur..."
          value={recherche}
          onChange={e => setRecherche(e.target.value)} />
        <select style={s.select} value={filtreFiliale} onChange={e => setFiltreFiliale(e.target.value)}>
          <option value="">Toutes les filiales</option>
          {filiales.map(f => <option key={f} value={f}>{f}</option>)}
        </select>
        <select style={s.select} value={filtreType} onChange={e => setFiltreType(e.target.value)}>
          <option value="">Tous les types</option>
          {Object.entries(TYPES).map(([k, v]) => (
            <option key={k} value={k}>{v.label}</option>
          ))}
        </select>
        {(recherche || filtreFiliale || filtreType) && (
          <button onClick={() => { setRecherche(''); setFiltreFiliale(''); setFiltreType(''); }}
            style={{ padding: '8px 14px', borderRadius: 8, border: '0.5px solid #ddd', background: '#fff', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit', color: '#888' }}>
            <IconeFermer taille={12} /> Réinitialiser
          </button>
        )}
        <span style={{ fontSize: 12, color: '#888', marginLeft: 'auto' }}>
          {archivesFiltrees.length} résultat{archivesFiltrees.length > 1 ? 's' : ''}
        </span>
      </div>

      {/* LISTE */}
      <div style={s.content}>
        {loading ? (
          <div style={s.emptyState}>
            <div style={{ marginBottom: 12, color: '#bbb' }}><IconeHorloge taille={36} /></div>
            Chargement...
          </div>
        ) : archivesFiltrees.length === 0 ? (
          <div style={s.emptyState}>
            <div style={{ marginBottom: 14, color: '#ccc' }}><IconeDossier taille={56} /></div>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#0D1B2A', marginBottom: 6 }}>
              Aucun dossier archivé
            </div>
            <div style={{ fontSize: 13, color: '#888' }}>
              Les dossiers approuvés par le PDG apparaîtront ici
            </div>
          </div>
        ) : (
          <div style={s.table}>
            {/* Header */}
            <div style={s.tableHeader}>
              <span>Référence</span>
              <span>Objet</span>
              <span>Demandeur</span>
              <span>Filiale</span>
              <span>Montant</span>
              <span>Date création</span>
              <span>Date approbation</span>
              <span></span>
            </div>
            {/* Lignes */}
            {archivesFiltrees.map(a => {
              const type = TYPES[a.type_document] || { icon: IconeDocument, color: '#666' };
              return (
                <div key={a.id} style={s.tableRow} onClick={() => navigate('/demandes/' + a.id)}>
                  {/* Référence */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ width: 32, height: 32, borderRadius: 8, background: type.color + '15', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>
                      <type.icon taille={22} />
                    </div>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#0D1B2A' }}>{a.reference}</div>
                      <div style={{ fontSize: 10, color: '#aaa', display: 'flex', alignItems: 'center', gap: 4 }}><type.icon taille={10} /> {TYPES[a.type_document]?.label || a.type_document}</div>
                    </div>
                  </div>
                  {/* Objet */}
                  <div style={{ fontSize: 12, color: '#555', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {a.titre || a.motif || '—'}
                  </div>
                  {/* Demandeur */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <div style={{ width: 26, height: 26, borderRadius: '50%', background: '#C0392B', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, color: '#fff', fontWeight: 700, flexShrink: 0 }}>
                      {a.demandeur?.prenom?.[0]}{a.demandeur?.nom?.[0]}
                    </div>
                    <span style={{ fontSize: 12 }}>{a.demandeur?.prenom} {a.demandeur?.nom}</span>
                  </div>
                  {/* Filiale */}
                  <span style={{ fontSize: 11, fontWeight: 700, background: '#0D1B2A', color: '#fff', padding: '2px 8px', borderRadius: 6, alignSelf: 'center' }}>
                    {a.filiale?.code || '—'}
                  </span>
                  {/* Montant */}
                  <span style={{ fontSize: 14, fontWeight: 800, color: '#27AE60' }}>
                    {formatMontant(a.montant)}
                  </span>
                  {/* Date création */}
                  <span style={{ fontSize: 11, color: '#888' }}>{formatDate(a.created_at)}</span>
                  {/* Date approbation */}
                  <span style={{ fontSize: 11, color: '#27AE60', fontWeight: 600 }}>
                    <IconeValide taille={11} /> {formatDateHeure(a.updated_at)}
                  </span>
                  {/* Action */}
                  <button onClick={e => { e.stopPropagation(); navigate('/demandes/' + a.id); }}
                    style={{ padding: '6px 14px', borderRadius: 8, border: '0.5px solid #27AE60', background: '#EAFAF1', color: '#1E8449', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600 }}>
                    Voir →
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

const s = {
  page: { minHeight: '100vh', background: '#F0F2F5', fontFamily: 'Inter, system-ui, sans-serif' },
  header: { background: '#fff', borderBottom: '0.5px solid #e5e7eb', padding: '16px 28px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  backBtn: { padding: '8px 16px', borderRadius: 8, border: '0.5px solid #ddd', background: '#fff', cursor: 'pointer', fontSize: 13, fontFamily: 'inherit' },
  filtres: { background: '#fff', borderBottom: '0.5px solid #e5e7eb', padding: '12px 28px', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' },
  search: { flex: 1, minWidth: 250, padding: '8px 14px', borderRadius: 8, border: '0.5px solid #ddd', fontSize: 13, fontFamily: 'inherit', outline: 'none' },
  select: { padding: '8px 12px', borderRadius: 8, border: '0.5px solid #ddd', fontSize: 12, fontFamily: 'inherit', outline: 'none', background: '#fff' },
  content: { padding: '20px 28px' },
  emptyState: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '80px 20px', textAlign: 'center', color: '#888' },
  table: { background: '#fff', borderRadius: 14, border: '0.5px solid #e5e7eb', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' },
  tableHeader: { display: 'grid', gridTemplateColumns: '1.5fr 2fr 1.5fr 80px 140px 130px 150px 80px', gap: 12, padding: '10px 20px', background: '#F9FAFB', borderBottom: '0.5px solid #e5e7eb', fontSize: 10, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.06em' },
  tableRow: { display: 'grid', gridTemplateColumns: '1.5fr 2fr 1.5fr 80px 140px 130px 150px 80px', gap: 12, padding: '14px 20px', borderBottom: '0.5px solid #f0f0f0', alignItems: 'center', cursor: 'pointer', background: '#fff', transition: '0.1s' },
};